import type { CarouselCardSummary, FormatId } from '../types/api';

interface Props {
  cards: CarouselCardSummary[];
}

/**
 * Horizontal-scrolling strip of card thumbnails, grouped by platform.
 *
 * For each platform we show all N+2 cards in cover → stories → CTA order.
 * Mirrors the composer.css `.carousel-card` styling from the design handoff
 * (drop shadow, dark frame, 1px white border).
 */
export function CarouselPreview({ cards }: Props) {
  if (cards.length === 0) {
    return <div className="empty muted">No cards yet — click Render.</div>;
  }
  const byPlatform = groupBy(cards, (c) => c.platform);
  return (
    <div className="preview">
      {Array.from(byPlatform.entries()).map(([platform, platformCards]) => (
        <div key={platform} className="preview-platform">
          <div className="preview-platform-head">
            <span className="preview-platform-label">{platform}</span>
            <span className="preview-platform-meta">
              {platformCards.length} cards · {platformCards[0].width} ×{' '}
              {platformCards[0].height}
            </span>
          </div>
          <div className="preview-strip">
            {platformCards.map((card) => (
              <div
                key={`${card.platform}-${card.cardIndex}`}
                className="preview-card"
                style={cardThumbStyle(platformCards[0].width, platformCards[0].height)}
              >
                {card.gcsUrl ? (
                  <img
                    src={card.gcsUrl}
                    alt={`${platform} ${card.kind} ${card.cardIndex}`}
                    loading="lazy"
                  />
                ) : (
                  <div className="preview-card-empty">no url</div>
                )}
                <div className="preview-card-meta">
                  {String(card.cardIndex).padStart(2, '0')} · {card.kind}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function groupBy<T, K extends string>(
  items: T[],
  keyOf: (t: T) => K,
): Map<K, T[]> {
  const out = new Map<K, T[]>();
  for (const item of items) {
    const k = keyOf(item);
    const arr = out.get(k);
    if (arr) arr.push(item);
    else out.set(k, [item]);
  }
  return out;
}

function cardThumbStyle(
  nativeWidth: number,
  nativeHeight: number,
): React.CSSProperties {
  // Display card at a max edge of 200px while preserving aspect ratio.
  const maxEdge = 200;
  const ratio = nativeWidth / nativeHeight;
  if (ratio >= 1) {
    return { width: `${maxEdge}px`, height: `${Math.round(maxEdge / ratio)}px` };
  }
  return { width: `${Math.round(maxEdge * ratio)}px`, height: `${maxEdge}px` };
}

/** Unused for now but exported for future filters by platform. */
export function platformLabel(id: FormatId): string {
  return id;
}
